import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, testConnection } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState('checking')

  useEffect(() => {
    let mounted = true

    // Test connection first
    const initializeAuth = async () => {
      try {
        console.log('Testing Supabase connection...')
        const isConnected = await testConnection()
        
        if (!isConnected) {
          console.error('Supabase connection failed')
          setConnectionStatus('failed')
          if (mounted) setLoading(false)
          return
        }

        console.log('Supabase connected successfully')
        setConnectionStatus('connected')

        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (mounted) {
          if (error) {
            console.error('Error getting session:', error)
          } else {
            console.log('Initial session:', session?.user?.email || 'No session')
          }
          setUser(session?.user ?? null)
          setLoading(false)
        }
      } catch (error) {
        console.error('Error in auth initialization:', error)
        setConnectionStatus('failed')
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (mounted) {
          console.log('Auth state change:', event, session?.user?.email || 'No user')
          setUser(session?.user ?? null)
          setLoading(false)
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true)
      console.log('=== SIGNUP ATTEMPT ===')
      console.log('Email:', email)
      console.log('Connection status:', connectionStatus)
      
      // Pre-flight checks
      if (connectionStatus !== 'connected') {
        console.error('Supabase not connected')
        throw new Error('Connection error. Please refresh the page and try again.')
      }

      // Validate inputs
      if (!email?.trim() || !password?.trim()) {
        throw new Error('Email and password are required')
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email.trim())) {
        throw new Error('Please enter a valid email address')
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters long')
      }

      // Clean email
      const cleanEmail = email.toLowerCase().trim()
      
      console.log('Calling Supabase signUp with:', {
        email: cleanEmail,
        hasPassword: !!password,
        userData
      })
      
      // Attempt signup with minimal options to avoid conflicts
      const { data, error } = await supabase.auth.signUp({
        email: cleanEmail,
        password: password,
        options: {
          data: userData,
          // Don't require email confirmation
          emailRedirectTo: undefined
        }
      })

      console.log('Supabase signUp response:', { 
        hasData: !!data, 
        hasUser: !!data?.user,
        userId: data?.user?.id,
        error: error?.message 
      })

      if (error) {
        console.error('SignUp error details:', {
          message: error.message,
          status: error.status,
          code: error.code || error.error_code
        })
        
        // Handle specific error cases with better messages
        if (error.message?.includes('User already registered')) {
          throw new Error('An account with this email already exists. Please sign in instead.')
        }
        
        if (error.message?.includes('Invalid email')) {
          throw new Error('Please enter a valid email address.')
        }
        
        if (error.message?.includes('Password should be at least')) {
          throw new Error('Password must be at least 6 characters long.')
        }
        
        if (error.message?.includes('signup is disabled') || 
            error.message?.includes('Signups not allowed')) {
          throw new Error('Account creation is currently disabled. Please contact support.')
        }
        
        if (error.message?.includes('Email rate limit exceeded') ||
            error.message?.includes('rate limit')) {
          throw new Error('Too many attempts. Please wait 5 minutes before trying again.')
        }

        // Network/connection errors
        if (error.message?.includes('Failed to fetch') ||
            error.message?.includes('NetworkError') ||
            error.message?.includes('fetch')) {
          throw new Error('Network error. Please check your internet connection and try again.')
        }

        // Database/server errors
        if (error.message?.toLowerCase().includes('database') || 
            error.message?.toLowerCase().includes('internal server error') ||
            error.message?.toLowerCase().includes('server error') ||
            error.status >= 500) {
          throw new Error('Server error during account creation. Please try again in a moment.')
        }

        // Auth configuration errors
        if (error.message?.includes('Invalid API key') ||
            error.message?.includes('JWT')) {
          throw new Error('Authentication service error. Please refresh the page and try again.')
        }
        
        // Generic error with original message for debugging
        throw new Error(error.message || 'Failed to create account. Please try again.')
      }

      // Validate response
      if (!data) {
        throw new Error('Invalid response from server. Please try again.')
      }

      if (!data.user) {
        console.error('No user in signup response:', data)
        throw new Error('Account creation failed. Please try again.')
      }

      console.log('✅ User created successfully:', {
        id: data.user.id,
        email: data.user.email,
        confirmed: data.user.email_confirmed_at ? 'Yes' : 'No'
      })
      
      return { data, error: null }

    } catch (err) {
      console.error('SignUp exception:', err)
      
      // Handle network errors specifically
      if (err.name === 'TypeError' && err.message?.includes('fetch')) {
        return { 
          data: null, 
          error: { message: 'Network error. Please check your connection and try again.' } 
        }
      }
      
      if (err.message?.includes('timeout')) {
        return { 
          data: null, 
          error: { message: 'Request timed out. Please try again.' } 
        }
      }
      
      return { 
        data: null, 
        error: { message: err.message || 'An unexpected error occurred. Please try again.' } 
      }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      console.log('=== SIGNIN ATTEMPT ===')
      console.log('Email:', email)
      
      if (connectionStatus !== 'connected') {
        throw new Error('Connection error. Please refresh the page and try again.')
      }
      
      const cleanEmail = email.toLowerCase().trim()
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password
      })

      if (error) {
        console.error('SignIn error:', error)
        
        if (error.message?.includes('Invalid login credentials') || 
            error.message?.includes('Invalid email or password') ||
            error.message?.includes('Email not confirmed')) {
          return { 
            data, 
            error: { message: 'Invalid email or password. Please check your credentials and try again.' } 
          }
        }
        
        return { data, error: { message: error.message } }
      }

      console.log('✅ SignIn successful:', data.user?.email)
      return { data, error }
      
    } catch (err) {
      console.error('SignIn exception:', err)
      return { 
        data: null, 
        error: { message: err.message || 'Network error. Please check your connection and try again.' } 
      }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signOut()
      return { error }
    } catch (err) {
      console.error('SignOut exception:', err)
      return { error: err }
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      })
      return { data, error }
    } catch (err) {
      console.error('UpdateProfile exception:', err)
      return { data: null, error: err }
    }
  }

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    connectionStatus
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}